import { FunctionComponent, SVGAttributes, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { H4, P3, Title3 } from "@components/new/Typo";
import { ButtonType } from "@components/new/Button";
import SuccessIcon from "@assets/icons/solid/circle-check.svg?react";
import XIcon from "@assets/icons/solid/circle-x.svg?react";
import { Modal, ModalSize } from "@components/new/Modal";
import styles from "./MessageModalNew.module.scss";

type TVariants = "warning" | "success" | "info";

interface MessageModalProps {
  windowTitle: string;
  title: string;
  description: string;
  btnTitle?: string;
  onClose: () => void;
  variant?: TVariants;
  size?: ModalSize;
}

const getIcon = (variant: string) => {
  const icons: Record<string, FunctionComponent<SVGAttributes<SVGElement>>> = {
    warning: XIcon,
    success: SuccessIcon,
  }
  return icons[variant] ?? "";
}

export const MessageModalContent = ({
  title,
  description,
  variant,
  Icon,
  iconSize,
  footer,
  className,
  descriptionClassName,
}: {
  title: string;
  description: string;
  Icon?: ReactNode;
  variant?: TVariants;
  iconSize?: "l" | "s";
  footer?: ReactNode;
  className?: string;
  descriptionClassName?: string;
}) => {
  const IconComponent = getIcon(variant ?? "success");

  const Description = variant === "info" ? Title3 : P3;

  return (
    <div className={cn(
      styles.content,
      styles[`variant-${variant}`],
      className,
    )}>
      <div className={cn(
        styles.icon,
        styles[`iconSize-${iconSize ?? "s"}`],
      )}>
        {Icon ? (Icon) : (<IconComponent />)}
      </div>
      {title ? (<H4>{title}</H4>) : null}
      {description ? (
        <Description className={cn(styles.description, descriptionClassName)} dangerouslySetInnerHTML={{
          __html: description,
        }} />
      ) : null}
      {footer}
    </div>
  );
};

const MessageModal: React.FC<MessageModalProps> = (props: MessageModalProps): React.ReactElement => {
  const { t } = useTranslation();
  return (
    <Modal
      title={props.windowTitle}
      size={props.size ?? ModalSize.S}
      isOpen={true}
      onClose={props.onClose}
      footerButtons={[
        {
          type: ButtonType.SECONDARY,
          title: props.btnTitle ?? t('modal.messageModal.btn'),
          onClick: props.onClose,
        },
      ]}
    >
      <MessageModalContent {...props} />
    </Modal>
  );
}

export default MessageModal;
